import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActiveDescendantKeyManager, FocusKeyManager, ListKeyManager } from '@angular/cdk/a11y';
import { ENTER } from '@angular/cdk/keycodes';
import { FormControl } from '@angular/forms';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { concat, Observable, of } from 'rxjs';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { orderBy, uniq } from 'lodash-es';
import { FilterSelectOption } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-select.model';
import { WindowEventsService } from '@nimbus-library/features/shared/services/window-events.service';
import { NimbusFilterHierarchySelectListItemComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-select-hierarchy-list-item/nimbus-filter-hierarchy-select-list-item.component';
import { FilterHierarchySelectOption } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-hirerarchy-select.model';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-filter-hierarchy-select-list',
  templateUrl: './nimbus-filter-hierarchy-select-list.component.html',
  styleUrls: ['./nimbus-filter-hierarchy-select-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterHierarchySelectListComponent implements AfterViewInit, OnChanges {
  @ViewChildren(NimbusFilterHierarchySelectListItemComponent) public items: QueryList<NimbusFilterHierarchySelectListItemComponent>;
  @ViewChild('searchInput') public searchInput: ElementRef<HTMLInputElement>;
  private keyManager: ListKeyManager<NimbusFilterHierarchySelectListItemComponent>;
  public selectedOptionsLookup: Record<string, boolean> = {};
  public hasSelection: boolean = false;
  public selection: FilterSelectOption[];

  public searchControl: FormControl<string> = new FormControl<string>('');

  public ICONS = ICONS;

  public trackByFn = (index: number, item: FilterHierarchySelectOption) => item.id;
  @Input() public options: FilterHierarchySelectOption[];

  @HostBinding('class.no-search') public noSearch: boolean = false;
  @Input() public set selectedOptions(val: string[]) {
    this.selectedOptionsLookup = val.reduce((acc, curr) => {
      acc[curr] = true;
      return acc;
    }, {});
    this.hasSelection = val.length > 0;
  }
  @Input() public focusable: boolean = true;
  @Input() public typeAheadEnabled: boolean = true;
  @Output() public selectionChanged: EventEmitter<FilterHierarchySelectOption[]> = new EventEmitter();
  @HostListener('keydown', ['$event'])
  public manage(event: KeyboardEvent): void {
    this.keyManager.onKeydown(event);
  }

  public options$: Observable<FilterHierarchySelectOption[]>;

  public treeControl = new FlatTreeControl<any>(
    (node) => node.level,
    (node) => node.expandable,
  );

  public treeFlattener = new MatTreeFlattener(
    this.transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children,
  );

  public flattenedData: FilterHierarchySelectOption[] = [];

  public dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  public constructor(private readonly windowEventsService: WindowEventsService) {}

  public ngAfterViewInit(): void {
    this.noSearch = this.flattenedData.length <= 2;
    const keyManagerBase = (this.focusable ? new FocusKeyManager(this.items) : new ActiveDescendantKeyManager(this.items)).withWrap();
    this.keyManager = this.typeAheadEnabled ? keyManagerBase.withTypeAhead() : keyManagerBase;

    this.windowEventsService
      .getKeyDown$()
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        this.keyManager.onKeydown(event);
        if (event.keyCode === ENTER && this.keyManager.activeItem) {
          this.keyManager.activeItem.setClickStyle();
        }
      });

    this.windowEventsService
      .getKeyUp$()
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (event.keyCode === ENTER && this.keyManager.activeItem) {
          this.keyManager.activeItem.onClick(event);
        }
      });

    this.options$ = concat(of(''), this.searchControl.valueChanges).pipe(
      untilDestroyed(this),
      distinctUntilChanged(),
      map((value) => {
        if (value) {
          return this.flattenedData.filter((option) => option.name.toLowerCase().includes(value.toLowerCase()));
        } else {
          return this.flattenedData;
        }
      }),
    );

    this.selection = orderBy(
      this.flattenedData.filter((option) => this.selectedOptionsLookup[option.id]),
      (o) => o.name.toLowerCase(),
      'asc',
    );

    this.searchInput?.nativeElement?.focus();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.dataSource.data = this.options;
      this.flattenedData = (this.dataSource as any)._flattenedData.value;
    }
  }

  public toggleSelection(): void {
    if (this.hasSelection) {
      this.clearAll();
    } else {
      this.selectAll(this.flattenedData);
    }
  }

  public onFilterSelect(
    { selected, option, withChildren }: { selected: boolean; option: FilterSelectOption; withChildren: boolean },
    ignoreChildren?: boolean,
  ): void {
    const selectedItems = withChildren && !ignoreChildren ? [option, ...this.treeControl.getDescendants(option)] : [option];
    const selection = this.flattenedData.filter((o) => this.selectedOptionsLookup[o.id]);
    if (selected) {
      this.selectionChanged.emit(uniq([...selectedItems, ...selection]));
    } else {
      const selectionLookup = selectedItems.reduce((acc, curr) => {
        acc[curr.id] = true;
        return acc;
      }, {});

      this.selectionChanged.emit(uniq(selection.filter((selectedOption) => !selectionLookup[selectedOption.id])));
    }
  }

  public onRemoveSearch(): void {
    this.searchControl.setValue(null);
  }

  private selectAll(selection: FilterSelectOption[]): void {
    this.selectionChanged.emit(selection);
  }

  private clearAll(): void {
    this.selectionChanged.emit([]);
  }

  private transformer(
    node: FilterHierarchySelectOption,
    level: number,
  ): { expandable: boolean; id: string; name: string; level: number; parentId: string } {
    return {
      expandable: !!node.children && node.children.length > 0,
      id: node.id,
      name: node.name,
      level: node.level,
      parentId: node.parentId,
    };
  }

  protected readonly TranslationKeys = TranslationKeys;
}
